import React, { useState, useEffect } from 'react';
import { Check, RotateCcw, Lightbulb, Shuffle, Settings, Play, Edit3 } from 'lucide-react';

const ColorPatternExplorer = () => {
  // 颜色定义 - 只有三种实际颜色
  const colors = {
    yellow: '#FFD700',
    red: '#FF4444',
    blue: '#4444FF',
    empty: '#F5F5F5'  // 未填充状态
  };

  const [numCols, setNumCols] = useState(10); // 第一行的列数
  const [editMode, setEditMode] = useState(false); // 编辑模式
  const [showSettings, setShowSettings] = useState(false); // 显示设置面板
  
  // 生成随机第一行
  const generateRandomTopRow = (cols) => {
    const colorNames = ['yellow', 'red', 'blue'];
    return Array.from({ length: cols }, () => 
      colorNames[Math.floor(Math.random() * colorNames.length)]
    );
  };

  const [topRow, setTopRow] = useState(() => generateRandomTopRow(numCols));
  const [grid, setGrid] = useState([]);
  const [selectedColor, setSelectedColor] = useState('yellow');
  const [errors, setErrors] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [showHint, setShowHint] = useState(false);

  // 初始化网格
  const initializeGrid = (cols, firstRow) => {
    const numRows = cols; // 行数等于第一行的列数
    const grid = [];
    for (let row = 0; row < numRows; row++) {
      const rowLength = cols - row;
      const rowData = [];
      for (let col = 0; col < rowLength; col++) {
        if (row === 0) {
          rowData.push(firstRow[col]);
        } else {
          rowData.push('empty');
        }
      }
      grid.push(rowData);
    }
    return grid;
  };

  // 当列数或第一行改变时重新初始化网格
  useEffect(() => {
    setGrid(initializeGrid(numCols, topRow));
    setErrors([]);
    setIsComplete(false);
    setShowHint(false);
  }, [numCols, topRow]);

  // 正确的三色规律
  const getNextColor = (left, right) => {
    // 如果有空格，返回空
    if (left === 'empty' || right === 'empty') return 'empty';
    
    // 相同颜色保持不变
    if (left === right) return left;
    
    // 不同颜色变成第三种颜色
    const allColors = ['yellow', 'red', 'blue'];
    return allColors.find(color => color !== left && color !== right);
  };

  // 检查当前网格是否符合规律
  const checkGrid = () => {
    const newErrors = [];
    
    for (let row = 1; row < grid.length; row++) {
      for (let col = 0; col < grid[row].length; col++) {
        const leftParent = grid[row - 1][col];
        const rightParent = grid[row - 1][col + 1];
        const expectedColor = getNextColor(leftParent, rightParent);
        const actualColor = grid[row][col];
        
        if (actualColor !== 'empty' && actualColor !== expectedColor) {
          newErrors.push(`${row}-${col}`);
        }
      }
    }
    
    setErrors(newErrors);
    
    // 检查是否完成
    const allFilled = grid.every((row, rowIndex) => 
      rowIndex === 0 || row.every(cell => cell !== 'empty')
    );
    
    if (allFilled && newErrors.length === 0) {
      setIsComplete(true);
    }
    
    return newErrors.length === 0;
  };

  // 处理格子点击
  const handleCellClick = (row, col) => {
    // 在探究模式下，第一行不能编辑
    if (!editMode && row === 0) return;
    
    // 在编辑模式下，所有格子都可以编辑
    // 在探究模式下，非第一行的格子都可以编辑
    
    const newGrid = [...grid];
    const currentColor = grid[row][col];
    
    if (currentColor === 'empty') {
      // 如果是空白格子，填充当前选中的颜色
      newGrid[row][col] = selectedColor;
    } else {
      // 如果已有颜色，按顺序切换到下一个颜色
      const colorOrder = ['yellow', 'red', 'blue'];
      const currentIndex = colorOrder.indexOf(currentColor);
      const nextIndex = (currentIndex + 1) % colorOrder.length;
      newGrid[row][col] = colorOrder[nextIndex];
    }
    
    setGrid(newGrid);
    
    // 如果修改的是第一行，更新topRow
    if (row === 0) {
      const newTopRow = [...topRow];
      newTopRow[col] = newGrid[row][col];
      setTopRow(newTopRow);
    }
    
    setErrors([]); // 清除错误提示
    setIsComplete(false); // 重置完成状态
  };

  // 随机生成新的第一行
  const randomizeTopRow = () => {
    const newTopRow = generateRandomTopRow(numCols);
    setTopRow(newTopRow);
    setEditMode(false); // 退出编辑模式，开始探究
  };

  // 改变列数
  const changeNumCols = (newCols) => {
    const clampedCols = Math.max(3, Math.min(15, newCols)); // 限制在3-15之间
    setNumCols(clampedCols);
    
    // 调整第一行长度
    let newTopRow = [...topRow];
    if (newTopRow.length < clampedCols) {
      // 如果需要更多列，随机生成
      while (newTopRow.length < clampedCols) {
        const colorNames = ['yellow', 'red', 'blue'];
        newTopRow.push(colorNames[Math.floor(Math.random() * colorNames.length)]);
      }
    } else if (newTopRow.length > clampedCols) {
      // 如果列数减少，截断
      newTopRow = newTopRow.slice(0, clampedCols);
    }
    setTopRow(newTopRow);
  };

  // 重置网格（清空除第一行外的所有内容）
  const resetGrid = () => {
    setGrid(initializeGrid(numCols, topRow));
    setErrors([]);
    setIsComplete(false);
    setShowHint(false);
    setEditMode(false);
  };

  // 自动填充一行（作为提示功能）
  const fillNextRow = () => {
    const newGrid = [...grid];
    
    for (let row = 1; row < grid.length; row++) {
      const hasEmpty = newGrid[row].some(cell => cell === 'empty');
      if (hasEmpty) {
        for (let col = 0; col < newGrid[row].length; col++) {
          if (newGrid[row][col] === 'empty') {
            const leftParent = newGrid[row - 1][col];
            const rightParent = newGrid[row - 1][col + 1];
            newGrid[row][col] = getNextColor(leftParent, rightParent);
          }
        }
        break; // 只填充一行
      }
    }
    
    setGrid(newGrid);
    setShowHint(true);
  };

  // 正方形样式 - 简洁美观
  const cellStyle = (color, isError, isClickable) => ({
    width: '32px',
    height: '32px',
    backgroundColor: colors[color],
    border: `2px solid ${isError ? '#EF4444' : (isClickable ? '#9CA3AF' : '#6B7280')}`,
    borderRadius: '6px',
    cursor: isClickable ? 'pointer' : 'default',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    color: color === 'empty' ? '#6B7280' : '#FFFFFF',
    textShadow: color === 'empty' ? 'none' : '1px 1px 2px rgba(0,0,0,0.5)',
    transition: 'all 0.2s ease',
    boxShadow: isError ? '0 0 10px rgba(239, 68, 68, 0.5)' : '0 2px 4px rgba(0,0,0,0.1)',
    opacity: isClickable ? 1 : (editMode ? 0.9 : 0.7),
  });

  const renderCell = (color, row, col) => {
    const isError = errors.includes(`${row}-${col}`);
    const isClickable = editMode || row > 0; // 编辑模式下所有格子可点击，探究模式下第一行以外的格子可点击
    
    return (
      <div
        key={`${row}-${col}`}
        style={cellStyle(color, isError, isClickable)}
        onClick={() => handleCellClick(row, col)}
        title={
          editMode 
            ? '编辑模式：点击切换颜色' 
            : (row === 0 ? '探究模式：第一行固定' : 
               (color === 'empty' ? `点击填充${selectedColor === 'yellow' ? '黄色' : selectedColor === 'red' ? '红色' : '蓝色'}` : '点击切换颜色'))
        }
        className={`
          ${isError ? 'animate-pulse' : ''}
          ${isClickable ? 'hover:scale-105 hover:shadow-lg' : ''}
        `}
      >
        {color === 'empty' && row > 0 ? '?' : ''}
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">
          最后一层的颜色
        </h1>
        <p className="text-center text-gray-600 mb-6">
          观察顶行颜色，发现规律，填充下面的三角形！
        </p>

        {/* 模式切换和设置 */}
        <div className="bg-white p-4 rounded-lg shadow-md mb-4">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              <span className="font-medium">当前模式：</span>
              <button
                onClick={() => setEditMode(!editMode)}
                className={`flex items-center gap-2 px-3 py-1 rounded ${
                  editMode 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-blue-500 text-white'
                }`}
              >
                {editMode ? <Edit3 size={16} /> : <Play size={16} />}
                {editMode ? '编辑模式' : '探究模式'}
              </button>
            </div>

            <button
              onClick={() => setShowSettings(!showSettings)}
              className="flex items-center gap-2 bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
            >
              <Settings size={16} />
              设置
            </button>

            <button
              onClick={randomizeTopRow}
              className="flex items-center gap-2 bg-purple-500 text-white px-3 py-1 rounded hover:bg-purple-600"
            >
              <Shuffle size={16} />
              随机第一行
            </button>
          </div>

          {/* 设置面板 */}
          {showSettings && (
            <div className="mt-4 p-4 bg-gray-50 rounded border">
              <h3 className="font-bold mb-3">三角形设置</h3>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <span>第一行列数：</span>
                  <input
                    type="number"
                    min="3"
                    max="15"
                    value={numCols}
                    onChange={(e) => changeNumCols(parseInt(e.target.value) || 3)}
                    className="w-16 px-2 py-1 border rounded"
                  />
                </label>
                <span className="text-sm text-gray-600">
                  (将生成 {numCols} 行的三角形)
                </span>
              </div>
            </div>
          )}
        </div>

        {/* 规律提示 */}
        {!editMode && (
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
            <h3 className="font-bold text-blue-800 mb-2">思考提示：</h3>
            <p className="text-blue-700 text-sm">
              🤔 观察相邻两个格子的颜色，它们如何决定下方格子的颜色？
              <br />
              💡 只有黄、红、蓝三种颜色，试试不同的组合会产生什么结果！
              <br />
              🎨 先选择颜色，再点击空白格子填充；点击已有颜色的格子可以切换颜色。
            </p>
          </div>
        )}

        {/* 编辑模式说明 */}
        {editMode && (
          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg mb-6">
            <h3 className="font-bold text-orange-800 mb-2">编辑模式：</h3>
            <p className="text-orange-700 text-sm">
              ✏️ 可以编辑任意格子的颜色，设计你自己的题目！
              <br />
              🎯 切换到探究模式开始解题。
            </p>
          </div>
        )}

        {/* 控制面板 */}
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              <span className="font-medium">颜色选择：</span>
              <div className="flex gap-2">
                {['yellow', 'red', 'blue'].map((colorName) => (
                  <button
                    key={colorName}
                    className={`w-8 h-8 border-2 rounded ${
                      selectedColor === colorName ? 'border-black shadow-lg' : 'border-gray-300'
                    } transition-all hover:scale-110`}
                    style={{ 
                      backgroundColor: colors[colorName],
                    }}
                    onClick={() => setSelectedColor(colorName)}
                    title={colorName}
                  />
                ))}
              </div>
            </div>
            
            {!editMode && (
              <>
                <button
                  onClick={checkGrid}
                  className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  <Check size={16} />
                  检查答案
                </button>
                
                <button
                  onClick={fillNextRow}
                  className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600"
                >
                  <Lightbulb size={16} />
                  填充提示
                </button>
              </>
            )}
            
            <button
              onClick={resetGrid}
              className="flex items-center gap-2 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              <RotateCcw size={16} />
              {editMode ? '清空' : '重置'}
            </button>
          </div>
        </div>

        {/* 三角形网格 - 清晰美观的排列 */}
        <div className="flex flex-col items-center gap-2 mb-6">
          {grid.map((row, rowIndex) => (
            <div 
              key={rowIndex} 
              className="flex gap-2"
            >
              {row.map((color, colIndex) => 
                renderCell(color, rowIndex, colIndex)
              )}
            </div>
          ))}
        </div>

        {/* 状态提示 */}
        {!editMode && errors.length > 0 && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <strong>发现错误！</strong> 有 {errors.length} 个格子不符合规律，请重新观察和调整。
            <br />
            <small>💡 记住：相同颜色保持不变，不同颜色变成第三种颜色</small>
          </div>
        )}

        {!editMode && isComplete && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            <strong>🎉 恭喜你！</strong> 你已经成功发现了颜色规律并完成了整个三角形！
            <br />
            <small>你掌握了一个重要的数学概念：三色细胞自动机！</small>
          </div>
        )}

        {!editMode && showHint && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <strong>提示已使用</strong> 观察刚才填充的格子，试着总结规律！
          </div>
        )}

        {/* 使用说明 */}
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h3 className="font-bold mb-2">功能说明：</h3>
          <div className="text-sm text-gray-600 space-y-2">
            <div>
              <strong>🎯 探究模式：</strong>
              <ul className="ml-4 space-y-1">
                <li>• 第一行颜色固定不可修改</li>
                <li>• 选择颜色后，点击空白格子填充该颜色</li>
                <li>• 点击已有颜色的格子可以切换到下一个颜色</li>
                <li>• 寻找相邻两色决定下方颜色的规律</li>
                <li>• 使用"检查答案"验证推理</li>
              </ul>
            </div>
            <div>
              <strong>✏️ 编辑模式：</strong>
              <ul className="ml-4 space-y-1">
                <li>• 可以编辑任意格子，包括第一行</li>
                <li>• 点击格子切换颜色，设计自己的题目</li>
                <li>• 调整三角形大小（3-15列）</li>
                <li>• 随机生成第一行开始新的探索</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default ColorPatternExplorer;
